import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Label,
  Select,
  Spinner,
  Textarea,
  TextInput,
} from "flowbite-react";
import {
  HiArrowLeft,
  HiOutlineExclamationCircle,
  HiOutlineShieldCheck,
  HiSparkles,
} from "react-icons/hi";
import { useQuery } from "react-query";
import axiosInstance from "../config/axiosInstance";
import { ADMIN, USERS } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import ForbiddenQueriesPermission from "../components/common/ForbiddenQueriesPermission";
import "./CreditEngineSettingsPage.css";

const cx = (...values) => values.filter(Boolean).join(" ");

const moneyFmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const DEFAULT_FORM_STATE = {
  autoReject: {
    protesto: false,
    protestoMinValue: "",
    chequeSemFundo: false,
    chequeMinCount: "",
    pendenciaFinanceira: false,
    pendenciaMinValue: "",
  },
  scoreThreshold: "",
  foundationMinAgeMonths: "",
  recency: {
    protesto: "",
    chequeSemFundo: "",
    pendenciaFinanceira: "",
    acaoCivil: "",
    recuperacaoJudicial: "",
    processosLesivos: "",
  },
  notes: "",
  advanced: {
    behavioral: {
      active: false,
      minScore: "",
    },
    location: {
      active: false,
      mode: "blocked",
      states: "",
    },
    companySize: {
      active: false,
      porte: "",
    },
    creditLimit: {
      active: false,
      minValue: "",
    },
    faturamento: {
      active: false,
      minValue: "",
    },
    age: {
      active: false,
      minAge: "",
      maxAge: "",
    },
  },
  legal: {
    acaoCivil: false,
    recuperacaoJudicial: false,
    processosLesivos: false,
    processosImageUrl: "",
  },
};

const currencyPattern = /^[0-9]+([.,][0-9]{0,2})?$/;
const digitsPattern = /^[0-9]*$/;

const toNumber = (raw) => {
  if (raw === "" || raw == null) return null;
  const normalized = String(raw)
    .replace(/[R$\s.]/g, "")
    .replace(",", ".");
  const n = Number(normalized);
  return Number.isNaN(n) ? null : n;
};

const formatCurrencyInput = (value) => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  const num = Number(digits) / 100;
  return moneyFmt.format(num);
};

const formatDateForDisplay = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("pt-BR");
};

const formatCurrency = (value) => {
  const num = toNumber(value);
  if (num == null) return null;
  return moneyFmt.format(num);
};

const RECENCY_OPTIONS = [
  { value: "", label: "Sem recência" },
  { value: "veryRecent", label: "Muito recente (<1 mês)" },
  { value: "recent", label: "Recente (1-3 meses)" },
  { value: "moderate", label: "Intermediário (3-6 meses)" },
  { value: "old", label: "Antigo (>6 meses)" },
];

const RECENCY_LABELS = RECENCY_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
);

const describeRecency = (value) =>
  value ? RECENCY_LABELS[value] || value : "Sem filtro de recência";
const appendRecency = (text, value) =>
  value ? `${text} (${describeRecency(value)})` : text;
const recencyPhrase = (value) => `Recência: ${describeRecency(value)}.`;

const getMonthsSinceDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  let years = now.getFullYear() - date.getFullYear();
  let months = now.getMonth() - date.getMonth();
  let days = now.getDate() - date.getDate();

  let totalMonths = years * 12 + months;
  if (days < 0) totalMonths -= 1;
  return totalMonths > 0 ? String(totalMonths) : "0";
};

const computeDateFromMonths = (months) => {
  const parsed = Number(months);
  if (Number.isNaN(parsed) || parsed <= 0) return null;
  const result = new Date();
  result.setMonth(result.getMonth() - parsed);
  return result.toISOString().split("T")[0];
};

const mapServerConfigToForm = (serverConfig) => {
  const safeNumber = (value) =>
    value != null && value !== "" ? String(value) : "";

  const advancedData = serverConfig?.advanced || {};
  const legalData = serverConfig?.legal || {};
  const recencyData = serverConfig?.recency || {};

  return {
    autoReject: {
      protesto: !!serverConfig?.autoRejectProtesto,
      protestoMinValue: safeNumber(serverConfig?.protestoMinValue),
      chequeSemFundo: !!serverConfig?.autoRejectChequeSemFundo,
      chequeMinCount: safeNumber(serverConfig?.chequeMinCount),
      pendenciaFinanceira: !!serverConfig?.autoRejectPendenciaFinanceira,
      pendenciaMinValue: safeNumber(serverConfig?.pendenciaMinValue),
    },
    scoreThreshold: safeNumber(serverConfig?.scoreThreshold),
    foundationMinAgeMonths: getMonthsSinceDate(serverConfig?.foundationMinDate),
    notes: serverConfig?.notes ?? "",
    advanced: {
      behavioral: {
        active: !!advancedData.behavioral?.active,
        minScore: safeNumber(advancedData.behavioral?.minScore),
      },
      location: {
        active: !!advancedData.location?.active,
        mode: advancedData.location?.mode || "blocked",
        states: advancedData.location?.states || "",
      },
      companySize: {
        active: !!advancedData.companySize?.active,
        porte: advancedData.companySize?.porte || "",
      },
      creditLimit: {
        active: !!advancedData.creditLimit?.active,
        minValue: safeNumber(advancedData.creditLimit?.minValue),
      },
      faturamento: {
        active: !!advancedData.faturamento?.active,
        minValue: safeNumber(advancedData.faturamento?.minValue),
      },
      age: {
        active: !!advancedData.age?.active,
        minAge: safeNumber(advancedData.age?.minAge),
        maxAge: safeNumber(advancedData.age?.maxAge),
      },
    },
    legal: {
      acaoCivil: !!legalData.acaoCivil,
      recuperacaoJudicial: !!legalData.recuperacaoJudicial,
      processosLesivos: !!legalData.processosLesivos,
      processosImageUrl: legalData.processosImageUrl || "",
    },
    recency: {
      protesto: recencyData.protesto || "",
      chequeSemFundo: recencyData.chequeSemFundo || "",
      pendenciaFinanceira: recencyData.pendenciaFinanceira || "",
      acaoCivil: recencyData.acaoCivil || "",
      recuperacaoJudicial: recencyData.recuperacaoJudicial || "",
      processosLesivos: recencyData.processosLesivos || "",
    },
  };
};

const LOCATION_MODE_LABELS = {
  blocked: "Bloquear",
  allowed: "Permitir",
};

const COMPANY_SIZE_LABELS = {
  micro: "Microempresa",
  pequena: "Empresa de Pequeno Porte",
  media: "Empresa de Médio Porte",
  grande: "Empresa de Grande Porte",
};

export default function CreditEngineSettingsPage() {
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [alert, setAlert] = useState(null);

  const { userPermission } = useApplication();
  const canEdit = useMemo(
    () => ["ADMIN", "POWER_USER"].includes(userPermission),
    [userPermission]
  );

  const navigate = useNavigate();

  const { isLoading: permissionsLoading, data: userPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axiosInstance.get(
        USERS.GET_USER_QUERY_PERMISSIONS
      );
      return items.data;
    }
  );

  const hasCreditEnginePermission = useMemo(() => {
    if (!Array.isArray(userPermissions)) return false;
    return userPermissions.some((item) => {
      const url = item?.url || "";
      if (typeof url !== "string") return false;
      return (
        url.includes("motor-credito-pf") || url.includes("motor-credito-pj")
      );
    });
  }, [userPermissions]);

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      setLoadingConfig(true);
      try {
        const { data } = await axiosInstance.get(ADMIN.GET_ENGINE_CREDIT);
        if (!mounted) return;
        const serverConfig = data?.data || data || {};
        setFormState(mapServerConfigToForm(serverConfig));
      } catch (error) {
        console.error(error);
        if (mounted) {
          setAlert({
            type: "error",
            message:
              "Não foi possível carregar as configurações do motor de crédito.",
          });
        }
      } finally {
        if (mounted) {
          setLoadingConfig(false);
        }
      }
    };

    loadConfig();

    return () => {
      mounted = false;
    };
  }, []);

  if (!permissionsLoading && !hasCreditEnginePermission) {
    return <ForbiddenQueriesPermission />;
  }

  const handleToggleAutoReject = (field) => (event) => {
    const checked = event.target.checked;
    setFormState((prev) => ({
      ...prev,
      autoReject: {
        ...prev.autoReject,
        [field]: checked,
      },
    }));
  };

  const handleScoreChange = (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      setFormState((prev) => ({ ...prev, scoreThreshold: value }));
    }
  };

  const handleAutoRejectCurrencyChange = (field) => (event) => {
    const { value } = event.target;
    const formatted = value === "" ? "" : formatCurrencyInput(value);
    setFormState((prev) => ({
      ...prev,
      autoReject: {
        ...prev.autoReject,
        [field]: formatted,
      },
    }));
  };

  const handleNumberField = (field) => (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      setFormState((prev) => ({
        ...prev,
        autoReject: {
          ...prev.autoReject,
          [field]: value,
        },
      }));
    }
  };

  const handleRecencyChange = (field) => (event) => {
    setFormState((prev) => ({
      ...prev,
      recency: {
        ...prev.recency,
        [field]: event.target.value,
      },
    }));
  };

  const handleFoundationAgeChange = (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      setFormState((prev) => ({
        ...prev,
        foundationMinAgeMonths: value,
      }));
    }
  };

  const handleNotesChange = (event) => {
    setFormState((prev) => ({ ...prev, notes: event.target.value }));
  };

  const handleAdvancedToggle = (section) => (event) => {
    const checked = event.target.checked;
    setFormState((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        [section]: {
          ...prev.advanced[section],
          active: checked,
        },
      },
    }));
  };

  const handleAdvancedChange = (section, field) => (value) => {
    setFormState((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        [section]: {
          ...prev.advanced[section],
          [field]: value,
        },
      },
    }));
  };

  const handleAdvancedCurrencyChange = (section, field) => (event) => {
    const { value } = event.target;
    const formatted = value === "" ? "" : formatCurrencyInput(value);
    handleAdvancedChange(section, field)(formatted);
  };

  const handleLegalToggle = (field) => (event) => {
    const checked = event.target.checked;
    setFormState((prev) => ({
      ...prev,
      legal: {
        ...prev.legal,
        [field]: checked,
      },
    }));
  };

  const handleLegalLinkChange = (event) => {
    setFormState((prev) => ({
      ...prev,
      legal: {
        ...prev.legal,
        processosImageUrl: event.target.value,
      },
    }));
  };

  const handleAdvancedNumericChange = (section, field) => (event) => {
    const { value } = event.target;
    if (value === "" || digitsPattern.test(value)) {
      handleAdvancedChange(section, field)(value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit) {
      setAlert({
        type: "warning",
        message: "Você não tem permissão para editar o motor de crédito.",
      });
      return;
    }

    setSavingConfig(true);
    setAlert(null);
    try {
      const payload = {
        autoRejectProtesto: !!formState.autoReject.protesto,
        autoRejectChequeSemFundo: !!formState.autoReject.chequeSemFundo,
        autoRejectPendenciaFinanceira:
          !!formState.autoReject.pendenciaFinanceira,
        protestoMinValue: toNumber(formState.autoReject.protestoMinValue),
        chequeMinCount:
          formState.autoReject.chequeMinCount === ""
            ? null
            : Number(formState.autoReject.chequeMinCount),
        pendenciaMinValue: toNumber(formState.autoReject.pendenciaMinValue),
        scoreThreshold:
          formState.scoreThreshold === ""
            ? null
            : Number(formState.scoreThreshold),
        foundationMinDate: computeDateFromMonths(
          formState.foundationMinAgeMonths
        ),
        notes: formState.notes || null,
        advanced: {
          behavioral: {
            active: !!formState.advanced.behavioral.active,
            minScore:
              formState.advanced.behavioral.minScore === ""
                ? null
                : Number(formState.advanced.behavioral.minScore),
          },
          location: {
            active: !!formState.advanced.location.active,
            mode: formState.advanced.location.mode,
            states:
              formState.advanced.location.states.trim() === ""
                ? null
                : formState.advanced.location.states.trim(),
          },
          companySize: {
            active: !!formState.advanced.companySize.active,
            porte:
              formState.advanced.companySize.porte.trim() === ""
                ? null
                : formState.advanced.companySize.porte.trim(),
          },
          creditLimit: {
            active: !!formState.advanced.creditLimit.active,
            minValue: toNumber(formState.advanced.creditLimit.minValue),
          },
          faturamento: {
            active: !!formState.advanced.faturamento.active,
            minValue: toNumber(formState.advanced.faturamento.minValue),
          },
          age: {
            active: !!formState.advanced.age.active,
            minAge:
              formState.advanced.age.minAge === ""
                ? null
                : Number(formState.advanced.age.minAge),
            maxAge:
              formState.advanced.age.maxAge === ""
                ? null
                : Number(formState.advanced.age.maxAge),
          },
        },
        legal: {
          acaoCivil: !!formState.legal.acaoCivil,
          recuperacaoJudicial: !!formState.legal.recuperacaoJudicial,
          processosLesivos: !!formState.legal.processosLesivos,
          processosImageUrl:
            formState.legal.processosImageUrl.trim() === ""
              ? null
              : formState.legal.processosImageUrl.trim(),
        },
        recency: {
          protesto: formState.recency.protesto || null,
          chequeSemFundo: formState.recency.chequeSemFundo || null,
          pendenciaFinanceira: formState.recency.pendenciaFinanceira || null,
          acaoCivil: formState.recency.acaoCivil || null,
          recuperacaoJudicial: formState.recency.recuperacaoJudicial || null,
          processosLesivos: formState.recency.processosLesivos || null,
        },
      };

      const { data } = await axiosInstance.put(
        ADMIN.SAVE_ENGINE_CREDIT,
        payload
      );
      const responseConfig = data?.data || data || payload;
      setFormState(mapServerConfigToForm(responseConfig));
      setAlert({
        type: "success",
        message: "Configurações do motor de crédito salvas com sucesso.",
      });
      window.dispatchEvent(new Event("credit-engine-config-updated"));
    } catch (error) {
      console.error(error);
      setAlert({
        type: "error",
        message: "Erro ao salvar as configurações. Tente novamente.",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const locationModeLabel =
    LOCATION_MODE_LABELS[formState.advanced.location.mode] ||
    (formState.advanced.location.mode === "allowed" ? "Permitir" : "Bloquear");
  const locationStatesText = formState.advanced.location.states.trim();
  const foundationAgeMonths = formState.foundationMinAgeMonths.trim();
  const companySizeLabel =
    COMPANY_SIZE_LABELS[formState.advanced.companySize.porte] ||
    formState.advanced.companySize.porte;

  return (
    <div>
      <div className="ces-header-actions">
        <Button
          className="bg-gray-500 text-white"
          onClick={() => navigate("/credit-engine")}
        >
          <div className="flex items-center gap-2">
            <HiArrowLeft className="w-4 h-4 mr-2" />
            <span>Voltar ao motor</span>
          </div>
        </Button>
      </div>
      <div className="credit-engine-settings-page">
        <div className="ces-shell">
          <header className="ces-header">
            <div>
              <h1 className="ces-title">Configuração avançada do motor</h1>
              <p className="ces-subtitle">
                Ajuste agora as regras que definem reprovações por pendências,
                protestos, score, comportamento, localização, porte da empresa,
                limites de crédito, faturamento presumido e idade.
              </p>
            </div>
          </header>

          {!canEdit && (
            <Alert color="warning" icon={HiOutlineExclamationCircle}>
              <span className="font-medium">Permissão limitada.</span> Apenas
              usuários ADMIN ou POWER_USER podem alterar as regras.
            </Alert>
          )}

          {alert && (
            <Alert
              color={
                alert.type === "success"
                  ? "success"
                  : alert.type === "error"
                  ? "failure"
                  : "warning"
              }
              onDismiss={() => setAlert(null)}
            >
              <span className="text-xs">{alert.message}</span>
            </Alert>
          )}

          {loadingConfig && (
            <div className="ces-loading">
              <Spinner size="md" />
              <p>Carregando configurações...</p>
            </div>
          )}

          {!loadingConfig && (
            <form className="ces-grid" onSubmit={handleSubmit}>
              <div className="ces-column">
                <section className="ces-card">
                  <div className="ces-card-header">
                    <div className="ces-card-icon">
                      <HiSparkles className="ces-card-icon-inner" />
                    </div>
                    <div>
                      <h2>Regras de reprovação automática</h2>
                      <p className="text-sm text-slate-500">
                        Controle protestos, cheques, pendências, score e
                        fundação em uma única camada de reprovação automática.
                      </p>
                    </div>
                  </div>
                  <div className="ces-card-body space-y-1">
                    <RuleCheckbox
                      label="Reprovar por protestos"
                      description="A reprovação dispara quando o total de protestos ultrapassa um valor."
                      checked={formState.autoReject.protesto}
                      onChange={handleToggleAutoReject("protesto")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="protestoMinValue"
                        value="Valor mínimo de protestos"
                        className="text-xs font-medium text-slate-700"
                      />
                      <TextInput
                        id="protestoMinValue"
                        type="text"
                        placeholder="Ex: 1.000,00"
                        value={formState.autoReject.protestoMinValue}
                        onChange={handleAutoRejectCurrencyChange(
                          "protestoMinValue"
                        )}
                        disabled={!canEdit || !formState.autoReject.protesto}
                        className="mt-2"
                      />
                      <div className="ces-recency-row">
                        <Label
                          value="Recência da última restrição"
                          className="text-xs font-medium text-slate-700 mt-4"
                        />
                        <Select
                          value={formState.recency.protesto}
                          onChange={handleRecencyChange("protesto")}
                          disabled={!canEdit || !formState.autoReject.protesto}
                          className="mt-1"
                        >
                          {RECENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Reprovar por cheques sem fundo"
                      description="Mostra a quantidade mínima de cheques que dispara a reprovação."
                      checked={formState.autoReject.chequeSemFundo}
                      onChange={handleToggleAutoReject("chequeSemFundo")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="chequeMinCount"
                        value="Quantidade mínima de cheques"
                        className="text-xs font-medium text-slate-700"
                      />
                      <TextInput
                        id="chequeMinCount"
                        type="text"
                        placeholder="Ex: 1, 2, 3..."
                        value={formState.autoReject.chequeMinCount}
                        onChange={handleNumberField("chequeMinCount")}
                        disabled={
                          !canEdit || !formState.autoReject.chequeSemFundo
                        }
                        className="mt-2"
                      />
                      <div className="ces-recency-row">
                        <Label
                          value="Recência da última restrição"
                          className="text-xs font-medium text-slate-700  mt-4"
                        />
                        <Select
                          value={formState.recency.chequeSemFundo}
                          onChange={handleRecencyChange("chequeSemFundo")}
                          disabled={
                            !canEdit || !formState.autoReject.chequeSemFundo
                          }
                          className="mt-1"
                        >
                          {RECENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Reprovar por pendências financeiras"
                      description="Abra a opção quando o valor total de pendências for perigoso."
                      checked={formState.autoReject.pendenciaFinanceira}
                      onChange={handleToggleAutoReject("pendenciaFinanceira")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="pendenciaMinValue"
                        value="Valor mínimo de pendências"
                        className="text-xs font-medium text-slate-700"
                      />
                      <TextInput
                        id="pendenciaMinValue"
                        type="text"
                        placeholder="Ex: 5.000,00"
                        value={formState.autoReject.pendenciaMinValue}
                        onChange={handleAutoRejectCurrencyChange(
                          "pendenciaMinValue"
                        )}
                        disabled={
                          !canEdit || !formState.autoReject.pendenciaFinanceira
                        }
                        className="mt-2"
                      />
                      <div className="ces-recency-row">
                        <Label
                          value="Recência da última restrição"
                          className="text-xs font-medium text-slate-700  mt-4"
                        />
                        <Select
                          value={formState.recency.pendenciaFinanceira}
                          onChange={handleRecencyChange("pendenciaFinanceira")}
                          disabled={
                            !canEdit ||
                            !formState.autoReject.pendenciaFinanceira
                          }
                          className="mt-1"
                        >
                          {RECENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </RuleCheckbox>

                    <div className="flex flex-col gap-4 md:flex-row">
                      <div className="flex-1">
                        <Label
                          htmlFor="scoreThreshold"
                          value="Score mínimo"
                          className="text-xs font-medium text-slate-700"
                        />
                        <TextInput
                          id="scoreThreshold"
                          type="text"
                          placeholder="Ex: 600"
                          value={formState.scoreThreshold}
                          onChange={handleScoreChange}
                          disabled={!canEdit}
                          className="mt-2"
                        />
                      </div>
                      <div className="flex-1">
                        <Label
                          htmlFor="foundationMinAgeMonths"
                          value="Idade mínima de fundação (meses)"
                          className="text-xs font-medium text-slate-700"
                        />
                        <TextInput
                          id="foundationMinAgeMonths"
                          type="text"
                          placeholder="Ex: 24"
                          value={formState.foundationMinAgeMonths}
                          onChange={handleFoundationAgeChange}
                          disabled={!canEdit}
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                </section>

                <section className="ces-card">
                  <div className="ces-card-header">
                    <div className="ces-card-icon">
                      <HiOutlineShieldCheck className="ces-card-icon-inner" />
                    </div>
                    <div>
                      <h2>Filtros avançados</h2>
                      <p className="text-sm text-slate-500">
                        Use regras complementares para comportamento,
                        localização, porte, limites e idade.
                      </p>
                    </div>
                  </div>

                  <div className="ces-card-body space-y-1">
                    <RuleCheckbox
                      label="Perfil comportamental"
                      description="Reprovar clientes com score comportamental abaixo do mínimo definido."
                      checked={formState.advanced.behavioral.active}
                      onChange={handleAdvancedToggle("behavioral")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="behavioralMinScore"
                        value="Score comportamental mínimo"
                        className="text-xs font-medium text-slate-700"
                      />
                      <TextInput
                        id="behavioralMinScore"
                        type="text"
                        placeholder="Ex: 40"
                        value={formState.advanced.behavioral.minScore}
                        onChange={handleAdvancedNumericChange(
                          "behavioral",
                          "minScore"
                        )}
                        disabled={
                          !canEdit || !formState.advanced.behavioral.active
                        }
                        className="mt-2"
                      />
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Localização"
                      description="Bloquear ou permitir apenas filtros por estados."
                      checked={formState.advanced.location.active}
                      onChange={handleAdvancedToggle("location")}
                      disabled={!canEdit}
                    >
                      <div className="space-y-3">
                        <Label
                          htmlFor="locationMode"
                          value="Modo"
                          className="text-xs font-medium text-slate-700"
                        />
                        <Select
                          id="locationMode"
                          value={formState.advanced.location.mode}
                          onChange={(event) =>
                            handleAdvancedChange(
                              "location",
                              "mode"
                            )(event.target.value)
                          }
                          disabled={
                            !canEdit || !formState.advanced.location.active
                          }
                          className="mt-1"
                        >
                          <option value="blocked">Estados bloqueados</option>
                          <option value="allowed">
                            Apenas estados permitidos
                          </option>
                        </Select>
                      </div>
                      <Label
                        htmlFor="locationStates"
                        value="UFs (separadas por vírgula)"
                        className="text-xs font-medium text-slate-700 mt-3"
                      />
                      <Textarea
                        id="locationStates"
                        placeholder="Ex: SP, RJ, MG"
                        rows={3}
                        value={formState.advanced.location.states}
                        onChange={(event) =>
                          handleAdvancedChange(
                            "location",
                            "states"
                          )(event.target.value)
                        }
                        disabled={
                          !canEdit || !formState.advanced.location.active
                        }
                        className="mt-2"
                      />
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Porte da empresa"
                      description="Exija um porte mínimo para aprovar automaticamente."
                      checked={formState.advanced.companySize.active}
                      onChange={handleAdvancedToggle("companySize")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="companySize"
                        value="Escolha o porte mínimo"
                        className="text-xs font-medium text-slate-700"
                      />
                      <Select
                        id="companySize"
                        value={formState.advanced.companySize.porte}
                        onChange={(event) =>
                          handleAdvancedChange(
                            "companySize",
                            "porte"
                          )(event.target.value)
                        }
                        disabled={
                          !canEdit || !formState.advanced.companySize.active
                        }
                        className="mt-2"
                      >
                        <option value="">Selecione</option>
                        <option value="micro">Microempresa</option>
                        <option value="pequena">
                          Empresa de Pequeno Porte
                        </option>
                        <option value="media">Empresa de Médio Porte</option>
                        <option value="grande">Empresa de Grande Porte</option>
                      </Select>
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Limite de crédito"
                      description="Estabeleça um limite mínimo de crédito para aprovação imediata."
                      checked={formState.advanced.creditLimit.active}
                      onChange={handleAdvancedToggle("creditLimit")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="creditLimit"
                        value="Limite mínimo (R$)"
                        className="text-xs font-medium text-slate-700"
                      />
                      <TextInput
                        id="creditLimit"
                        type="text"
                        placeholder="Ex: 20.000,00"
                        value={formState.advanced.creditLimit.minValue}
                        onChange={handleAdvancedCurrencyChange(
                          "creditLimit",
                          "minValue"
                        )}
                        disabled={
                          !canEdit || !formState.advanced.creditLimit.active
                        }
                        className="mt-2"
                      />
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Faturamento presumido"
                      description="Considere apenas empresas com faturamento presumido acima do mínimo."
                      checked={formState.advanced.faturamento.active}
                      onChange={handleAdvancedToggle("faturamento")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="faturamento"
                        value="Faturamento mínimo (R$)"
                        className="text-xs font-medium text-slate-700"
                      />
                      <TextInput
                        id="faturamento"
                        type="text"
                        placeholder="Ex: 150.000,00"
                        value={formState.advanced.faturamento.minValue}
                        onChange={handleAdvancedCurrencyChange(
                          "faturamento",
                          "minValue"
                        )}
                        disabled={
                          !canEdit || !formState.advanced.faturamento.active
                        }
                        className="mt-2"
                      />
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Restrição de idade"
                      description="Reprova automaticamente pessoas fora da faixa de idade."
                      checked={formState.advanced.age.active}
                      onChange={handleAdvancedToggle("age")}
                      disabled={!canEdit}
                    >
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label
                            htmlFor="minAge"
                            value="Idade mínima"
                            className="text-xs font-medium text-slate-700"
                          />
                          <TextInput
                            id="minAge"
                            type="text"
                            placeholder="Ex: 18"
                            value={formState.advanced.age.minAge}
                            onChange={handleAdvancedNumericChange(
                              "age",
                              "minAge"
                            )}
                            disabled={
                              !canEdit || !formState.advanced.age.active
                            }
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="maxAge"
                            value="Idade máxima"
                            className="text-xs font-medium text-slate-700"
                          />
                          <TextInput
                            id="maxAge"
                            type="text"
                            placeholder="Ex: 75"
                            value={formState.advanced.age.maxAge}
                            onChange={handleAdvancedNumericChange(
                              "age",
                              "maxAge"
                            )}
                            disabled={
                              !canEdit || !formState.advanced.age.active
                            }
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </RuleCheckbox>
                  </div>
                </section>
                <section className="ces-card">
                  <div className="ces-card-header">
                    <div className="ces-card-icon">
                      <HiOutlineShieldCheck className="ces-card-icon-inner" />
                    </div>
                    <div>
                      <h2>Monitoramento legal</h2>
                      <p className="text-sm text-slate-500">
                        Reprovar automaticamente por ocorrências judiciais
                        críticas.
                      </p>
                    </div>
                  </div>
                  <div className="ces-card-body space-y-4">
                    <RuleCheckbox
                      label="Ação cível"
                      description="Dispara quando houver registro de ação cível relevante."
                      checked={formState.legal.acaoCivil}
                      onChange={handleLegalToggle("acaoCivil")}
                      disabled={!canEdit}
                    >
                      <div className="ces-recency-row">
                        <Label
                          value="Recência da restrição"
                          className="text-xs font-medium text-slate-700"
                        />
                        <Select
                          value={formState.recency.acaoCivil}
                          onChange={handleRecencyChange("acaoCivil")}
                          disabled={!canEdit || !formState.legal.acaoCivil}
                          className="mt-1"
                        >
                          {RECENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Recuperação judicial"
                      description="Reprovar empresas em processo de recuperação judicial."
                      checked={formState.legal.recuperacaoJudicial}
                      onChange={handleLegalToggle("recuperacaoJudicial")}
                      disabled={!canEdit}
                    >
                      <div className="ces-recency-row">
                        <Label
                          value="Recência da restrição"
                          className="text-xs font-medium text-slate-700"
                        />
                        <Select
                          value={formState.recency.recuperacaoJudicial}
                          onChange={handleRecencyChange("recuperacaoJudicial")}
                          disabled={
                            !canEdit || !formState.legal.recuperacaoJudicial
                          }
                          className="mt-1"
                        >
                          {RECENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </RuleCheckbox>

                    <RuleCheckbox
                      label="Ações e processos lesivos"
                      description="Considere processos graves com provas visuais."
                      checked={formState.legal.processosLesivos}
                      onChange={handleLegalToggle("processosLesivos")}
                      disabled={!canEdit}
                    >
                      <Label
                        htmlFor="processosImageUrl"
                        value="Link da imagem/prova"
                        className="text-xs font-medium text-slate-700"
                      />
                      <TextInput
                        id="processosImageUrl"
                        type="text"
                        placeholder="https://..."
                        value={formState.legal.processosImageUrl}
                        onChange={handleLegalLinkChange}
                        disabled={!canEdit}
                        className="mt-2"
                      />
                      <div className="ces-recency-row mt-2">
                        <Label
                          value="Recência da restrição"
                          className="text-xs font-medium text-slate-700"
                        />
                        <Select
                          value={formState.recency.processosLesivos}
                          onChange={handleRecencyChange("processosLesivos")}
                          disabled={
                            !canEdit || !formState.legal.processosLesivos
                          }
                          className="mt-1"
                        >
                          {RECENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </RuleCheckbox>
                  </div>
                </section>
              </div>

              <div className="ces-side-column">
                <section className="ces-card ces-side-card">
                  <div className="ces-card-header">
                    <div>
                      <h2>Resumo e observações</h2>
                      <p className="text-sm text-slate-500">
                        Veja como o motor aplica cada regra de reprovação
                        automática.
                      </p>
                    </div>
                  </div>
                  <div className="ces-card-body space-y-5">
                    <div className="ces-summary-grid">
                      <p className="ces-summary-label">Situação atual</p>
                      <ul className="ces-summary-list">
                        <SummaryItem
                          active={formState.autoReject.protesto}
                          text={
                            formState.autoReject.protesto
                              ? appendRecency(
                                  formState.autoReject.protestoMinValue
                                    ? `Reprovar se protestos atingirem ${formatCurrency(
                                        formState.autoReject.protestoMinValue
                                      )}.`
                                    : "Reprovar quando houver qualquer protesto.",
                                  formState.recency.protesto
                                )
                              : "Protestos não estão sendo usados."
                          }
                        />
                        <SummaryItem
                          active={formState.autoReject.chequeSemFundo}
                          text={
                            formState.autoReject.chequeSemFundo
                              ? appendRecency(
                                  formState.autoReject.chequeMinCount
                                    ? `Reprovar a partir de ${formState.autoReject.chequeMinCount} cheque(s).`
                                    : "Reprovar com qualquer quantidade de cheques sem fundo.",
                                  formState.recency.chequeSemFundo
                                )
                              : "Cheques sem fundo não estão habilitados."
                          }
                        />
                        <SummaryItem
                          active={formState.autoReject.pendenciaFinanceira}
                          text={
                            formState.autoReject.pendenciaFinanceira
                              ? appendRecency(
                                  formState.autoReject.pendenciaMinValue
                                    ? `Reprovar se pendências atingirem ${formatCurrency(
                                        formState.autoReject.pendenciaMinValue
                                      )}.`
                                    : "Reprovar com qualquer pendência financeira.",
                                  formState.recency.pendenciaFinanceira
                                )
                              : "Pendências não estão habilitadas."
                          }
                        />
                        <SummaryItem
                          active={!!formState.scoreThreshold}
                          text={
                            formState.scoreThreshold
                              ? `Score mínimo ${formState.scoreThreshold}.`
                              : "Score não está sendo usado como critério."
                          }
                        />
                        <SummaryItem
                          active={Boolean(
                            foundationAgeMonths &&
                              Number(foundationAgeMonths) > 0
                          )}
                          text={
                            foundationAgeMonths
                              ? `Reprovar empresas com menos de ${foundationAgeMonths} meses de fundação.`
                              : "Sem restrição de fundação."
                          }
                        />
                        <SummaryItem
                          active={formState.advanced.behavioral.active}
                          text={
                            formState.advanced.behavioral.minScore
                              ? `Reprovar com comportamento abaixo de ${formState.advanced.behavioral.minScore}.`
                              : "Score comportamental não configurado."
                          }
                        />
                        <SummaryItem
                          active={formState.advanced.location.active}
                          text={
                            locationStatesText
                              ? `${locationModeLabel} os estados ${locationStatesText}.`
                              : "Sem lista de estados."
                          }
                        />
                        <SummaryItem
                          active={formState.advanced.companySize.active}
                          text={
                            companySizeLabel
                              ? `Porte mínimo: ${companySizeLabel}.`
                              : "Sem porte mínimo definido."
                          }
                        />
                        <SummaryItem
                          active={formState.advanced.creditLimit.active}
                          text={
                            formState.advanced.creditLimit.minValue
                              ? `Limite mínimo ${formatCurrency(
                                  formState.advanced.creditLimit.minValue
                                )}.`
                              : "Limite de crédito não configurado."
                          }
                        />
                        <SummaryItem
                          active={formState.advanced.faturamento.active}
                          text={
                            formState.advanced.faturamento.minValue
                              ? `Faturamento mínimo ${formatCurrency(
                                  formState.advanced.faturamento.minValue
                                )}.`
                              : "Sem faturamento presumido definido."
                          }
                        />
                        <SummaryItem
                          active={formState.advanced.age.active}
                          text={
                            formState.advanced.age.minAge ||
                            formState.advanced.age.maxAge
                              ? `Faixa de idade ${
                                  formState.advanced.age.minAge || "0"
                                } até ${formState.advanced.age.maxAge || "∞"}.`
                              : "Sem restrição de idade."
                          }
                        />
                        <SummaryItem
                          active={formState.legal.acaoCivil}
                          text={
                            formState.legal.acaoCivil
                              ? appendRecency(
                                  "Reprovar por ocorrência de ação cível.",
                                  formState.recency.acaoCivil
                                )
                              : "Ação cível não está sendo usada."
                          }
                        />
                        <SummaryItem
                          active={formState.legal.recuperacaoJudicial}
                          text={
                            formState.legal.recuperacaoJudicial
                              ? appendRecency(
                                  "Reprovar empresas em recuperação judicial.",
                                  formState.recency.recuperacaoJudicial
                                )
                              : "Recuperação judicial não está sendo usada."
                          }
                        />
                        <SummaryItem
                          active={formState.legal.processosLesivos}
                          text={
                            formState.legal.processosLesivos
                              ? appendRecency(
                                  formState.legal.processosImageUrl
                                    ? `Referência visual: ${formState.legal.processosImageUrl}.`
                                    : "Reprovar quando houver processos lesivos.",
                                  formState.recency.processosLesivos
                                )
                              : "Ações/processos não estão sendo usados."
                          }
                        />
                      </ul>
                    </div>
                    <div className="ces-summary-section">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-slate-900">
                          Observações internas
                        </h3>
                        <span className="text-sm">Opcional</span>
                      </div>
                      <Textarea
                        rows={4}
                        value={formState.notes}
                        onChange={handleNotesChange}
                        placeholder="Use este campo para juntar instruções, exceções ou contexto adicional."
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="ces-card-footer">
                      <Button type="submit" disabled={savingConfig || !canEdit}>
                        {savingConfig ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Salvando...
                          </>
                        ) : (
                          "Salvar configurações"
                        )}
                      </Button>
                    </div>
                  </div>
                </section>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function RuleCheckbox({
  label,
  description,
  checked,
  onChange,
  disabled,
  children,
}) {
  return (
    <label
      className={cx(
        "flex flex-col gap-3 rounded-xl border p-3 transition-all cursor-pointer",
        checked
          ? "border-indigo-500/70 bg-indigo-50/60"
          : "border-slate-200 bg-slate-50/60 hover:border-indigo-300 hover:bg-indigo-50/40",
        disabled && "opacity-60 cursor-not-allowed"
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="mt-0.5"
        />
        <div>
          <p className="text-sm font-medium text-slate-900">{label}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>

      {children && checked && <div className="px-1">{children}</div>}
    </label>
  );
}

function SummaryItem({ active, text }) {
  return (
    <li className="flex items-center gap-2 text-sm text-slate-600">
      <span
        className={cx(
          "h-2 w-2 rounded-full",
          active ? "bg-red-500" : "bg-gray-300"
        )}
      />
      <span>{text}</span>
    </li>
  );
}
